import { useState } from "react";
import { Link } from "react-router-dom";
import Footer from "@/components/feature/Footer";
import { useBooking } from "@/hooks/useBooking";
import StepIndicator from "./components/StepIndicator";
import BookingSummary from "./components/BookingSummary";
import StepService from "./components/StepService";
import StepHome from "./components/StepHome";
import StepSchedule from "./components/StepSchedule";
import StepCleaner from "./components/StepCleaner";
import StepConfirm from "./components/StepConfirm";
import BookingSuccess from "./components/BookingSuccess";

const STEPS = [
  { label: "Service" },
  { label: "Home" },
  { label: "Schedule" },
  { label: "Cleaner" },
  { label: "Confirm" },
];

export default function BookingPage() {
  const { state, update, toggleAddOn, summary, reset } = useBooking();
  const [current, setCurrent] = useState(0);
  const [confirmed, setConfirmed] = useState(false);

  const canContinue = (() => {
    if (current === 0) return !!state.serviceId && !!state.frequencyId;
    if (current === 1) return !!state.bedrooms && !!state.bathrooms;
    if (current === 2) return !!state.date && !!state.time;
    if (current === 3) return true; // null is "auto-match", valid
    if (current === 4) {
      const a = state.address;
      const c = state.contact;
      return !!a.line1 && !!a.city && !!a.state && !!a.zip && !!c.name && !!c.email && !!c.phone;
    }
    return false;
  })();

  const next = () => {
    if (current < STEPS.length - 1) setCurrent(current + 1);
    else setConfirmed(true);
  };
  const back = () => setCurrent((c) => Math.max(0, c - 1));

  if (confirmed) {
    return (
      <main className="min-h-screen bg-background-50 flex flex-col">
        <BookingHeader />
        <div className="flex-1 px-4 md:px-10">
          <BookingSuccess
            summary={summary}
            date={state.date}
            time={state.time}
            onReset={() => {
              reset();
              setCurrent(0);
              setConfirmed(false);
            }}
          />
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background-100">
      <BookingHeader />

      <div className="px-4 md:px-10 pt-8 pb-6">
        <StepIndicator
          current={current}
          steps={STEPS}
          onJump={(i) => i < current && setCurrent(i)}
        />
      </div>

      <section className="px-4 md:px-10 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10">
          <div className="lg:col-span-8 bg-background-50 border border-background-200/70 rounded-3xl p-6 md:p-10">
            {current === 0 && <StepService state={state} update={update} />}
            {current === 1 && <StepHome state={state} update={update} toggleAddOn={toggleAddOn} />}
            {current === 2 && <StepSchedule state={state} update={update} />}
            {current === 3 && <StepCleaner state={state} update={update} />}
            {current === 4 && <StepConfirm state={state} update={update} />}

            <div className="mt-10 pt-6 border-t border-background-200 flex flex-col sm:flex-row items-center justify-between gap-3">
              {current > 0 ? (
                <button
                  type="button"
                  onClick={back}
                  className="inline-flex items-center gap-2 text-foreground-800 text-sm font-medium px-5 py-3 rounded-full border border-foreground-300 hover:bg-background-100 whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back
                </button>
              ) : (
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 text-foreground-700 text-sm font-medium whitespace-nowrap hover:text-foreground-950"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to home
                </Link>
              )}
              <button
                type="button"
                disabled={!canContinue}
                onClick={next}
                className={`group inline-flex items-center gap-2 pl-6 pr-2 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  canContinue
                    ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                    : "bg-background-200 text-foreground-500 cursor-not-allowed"
                }`}
              >
                {current === STEPS.length - 1 ? "Confirm booking" : "Continue"}
                <span
                  className={`w-8 h-8 flex items-center justify-center rounded-full ${
                    canContinue ? "bg-background-50 text-primary-600" : "bg-background-50/40 text-foreground-400"
                  }`}
                >
                  <i className="ri-arrow-right-line"></i>
                </span>
              </button>
            </div>
          </div>

          <div className="lg:col-span-4">
            <BookingSummary state={state} summary={summary} />
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}

function BookingHeader() {
  return (
    <header className="bg-background-50/80 backdrop-blur-md border-b border-background-200/70 px-4 md:px-8 py-4 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2">
        <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500">
          <i className="ri-leaf-line text-lg text-background-50"></i>
        </span>
        <span className="font-heading text-xl font-semibold tracking-tight text-foreground-950">
          PureNest
        </span>
      </Link>
      <div className="flex items-center gap-2 text-xs text-foreground-700">
        <i className="ri-lock-line"></i>
        Secure booking
      </div>
    </header>
  );
}